import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api, { patientApi } from '../../lib/api';
import { submitFormResilient } from '../../lib/offlineQueue';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Check, X, Pen, RotateCcw, Save, Copy, Link2 } from 'lucide-react';

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + Number(n)); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * Sign-on-PDF mode — tablet/mobile friendly view where a clinician fills
 * form fields directly ON TOP of the original PDF page(s). Tapping a field
 * opens an inline input positioned at its bbox; signature fields pop a
 * canvas drawer. On submit the values are sent to /api/forms/{id}/submit.
 *
 * Route: /forms/:formId/sign-on-pdf?patientId=X&taskId=Y
 */
export default function SignOnPdfView() {
  const { formId } = useParams();
  const [params] = useSearchParams();
  const patientId = params.get('patientId');
  const taskId = params.get('taskId');
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [patient, setPatient] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signingField, setSigningField] = useState(null); // open signature modal
  const [schedule, setSchedule] = useState(null);
  const [nextDue, setNextDue] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/forms/${formId}/print-original`);
        setData(res.data);
        if (patientId) {
          try {
            const pRes = await patientApi.get(patientId);
            setPatient(pRes.data);
          } catch {}
          try {
            const pf = await api.get(`/forms/${formId}/prefill`, { params: { patient_id: patientId } });
            setValues(prev => ({ ...pf.data.values, ...prev }));
            setSchedule(pf.data.schedule);
            if (pf.data.interval_days) setNextDue(addDays(pf.data.interval_days));
          } catch {}
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load form');
      } finally {
        setLoading(false);
      }
    })();
  }, [formId, patientId]);

  const copyFromLast = async () => {
    try {
      const r = await api.get(`/forms/${formId}/print-original`, { params: { submission_id: schedule.last_submission_id } });
      const prev = {};
      (r.data.fields || []).forEach(f => { if (f._value !== undefined && f.field_type !== 'signature' && f.type !== 'signature') prev[f.field_id || f.id] = f._value; });
      setValues(cur => ({ ...prev, ...Object.fromEntries(Object.entries(cur).filter(([, v]) => v !== '' && v != null)) }));
      toast.success('Copied answers from the last update — review before submitting');
    } catch { toast.error('Could not load the last update'); }
  };

  const setValue = useCallback((fieldId, v) => {
    setValues(prev => ({ ...prev, [fieldId]: v }));
  }, []);

  const handleSubmit = async () => {
    if (!patientId) { toast.error('Patient is required to submit'); return; }
    // Validate required fields
    for (const f of data.fields || []) {
      if (f.required) {
        const v = values[f.field_id || f.id];
        if (v === undefined || v === null || v === '' || v === false) {
          toast.error(`"${f.label}" is required`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const responses = {};
      for (const f of data.fields || []) {
        const k = f.field_id || f.id;
        if (values[k] !== undefined) responses[k] = values[k];
      }
      const payload = { form_id: formId, patient_id: patientId, responses, next_update_due: nextDue || null };
      if (taskId) payload.task_id = taskId;
      const res = await submitFormResilient(api, formId, payload);
      if (res?.data?.offline) {
        toast.success('Saved offline — will sync when online');
      } else {
        const n = res?.data?.patient_fields_updated?.length || 0;
        toast.success(n ? `Submitted — ${n} patient record field${n > 1 ? 's' : ''} updated` : 'Submitted');
      }
      navigate(`/patients/${patientId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 animate-spin text-[#0E7490]" /></div>;
  if (!data || !data.pages?.length) return <div className="text-center py-12 text-sm text-[#94A3B8]">This form has no PDF layout. Open it in the standard form view instead.</div>;

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="sign-on-pdf-view">
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] p-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="outline" size="sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(patientId ? `/patients/${patientId}` : '/forms'))} data-testid="sign-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#0A2342] truncate">{data.name || 'Form'}</div>
            <div className="text-[11px] text-[#64748B] truncate">
              {patient ? `${patient.first_name} ${patient.last_name}` : 'Tap fields to fill · Draw signatures on the PDF'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {schedule && (
            <div className="text-[11px] text-[#64748B] text-right leading-tight" data-testid="sign-last-updated">
              Last updated {new Date(schedule.last_updated_at).toLocaleString()}<br />by {schedule.last_updated_by_name}
            </div>
          )}
          {schedule?.last_submission_id && (
            <Button variant="outline" size="sm" onClick={copyFromLast} data-testid="sign-copy-last-btn"><Copy className="w-4 h-4 mr-1" />Copy last update</Button>
          )}
          {patientId && (
            <label className="flex items-center gap-1.5 text-[11px] text-[#64748B]">Next update due
              <Input type="date" value={nextDue} onChange={e => setNextDue(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="sign-next-due-input" />
            </label>
          )}
        <Button
          className="careiq-btn-primary flex items-center gap-2"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="sign-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Submit
        </Button>
        </div>
      </div>
      {patientId && (data.fields || []).some(f => f.patient_field_ref) && (
        <div className="max-w-5xl mx-auto px-3 pt-3 text-[11px] text-[#0E7490] flex items-center gap-1.5" data-testid="sign-linked-note">
          <Link2 className="w-3.5 h-3.5" />Teal-outlined boxes are filled from the patient record — changes you make there will update the record (logged in its history).
        </div>
      )}

      {/* Pages with tappable overlays */}
      <div className="max-w-5xl mx-auto py-4 md:py-8 px-2 md:px-4 space-y-4 md:space-y-6">
        {data.pages.map((pageBase64, pageIdx) => {
          const fieldsOnPage = (data.fields || []).filter(f => (f.page_index ?? 0) === pageIdx);
          return (
            <InteractivePage
              key={pageIdx}
              pageBase64={pageBase64}
              fields={fieldsOnPage}
              values={values}
              onSetValue={setValue}
              onStartSigning={setSigningField}
            />
          );
        })}
      </div>

      {signingField && (
        <SignatureModal
          field={signingField}
          initialValue={values[signingField.field_id || signingField.id]}
          onClose={() => setSigningField(null)}
          onSave={(dataUrl) => {
            setValue(signingField.field_id || signingField.id, dataUrl);
            setSigningField(null);
          }}
        />
      )}
    </div>
  );
}

function InteractivePage({ pageBase64, fields, values, onSetValue, onStartSigning }) {
  return (
    <div className="relative bg-white shadow-md mx-auto rounded-md overflow-hidden" style={{ width: '100%', maxWidth: '850px', containerType: 'inline-size' }}>
      <div className="relative">
        <img src={`data:image/png;base64,${pageBase64}`} alt="Form page" className="block w-full h-auto select-none" draggable={false} />
        {fields.map(f => (
          <InteractiveField
            key={f.field_id || f.id}
            field={f}
            value={values[f.field_id || f.id]}
            onChange={(v) => onSetValue(f.field_id || f.id, v)}
            onStartSigning={() => onStartSigning(f)}
          />
        ))}
      </div>
    </div>
  );
}

function InteractiveField({ field, value, onChange, onStartSigning }) {
  const bbox = field.bbox || {};
  const fieldType = field.field_type || field.type || 'text';
  const fieldId = field.field_id || field.id;
  const boxStyle = {
    position: 'absolute',
    left: `${(bbox.x || 0) * 100}%`,
    top: `${(bbox.y || 0) * 100}%`,
    width: `${Math.max((bbox.w || 0.1), 0.04) * 100}%`,
    height: `${Math.max(bbox.h || 0.012, 0.012) * 100}%`,
    fontSize: 'max(10px, 1.3cqw)',
  };
  const ring = field.patient_field_ref ? 'ring-2 ring-[#0E7490]/70 bg-[#F0FDFA]/90' : 'ring-1 ring-[#94A3B8]/60 bg-white/90';

  // Signature field — button that opens canvas modal
  if (fieldType === 'signature') {
    const hasValue = !!value;
    return (
      <button
        type="button"
        onClick={onStartSigning}
        className={`flex items-center justify-center rounded-md transition-colors ${hasValue ? 'bg-[#F0FDFA] ring-2 ring-[#0E7490]' : 'bg-[#FEF3C7]/50 hover:bg-[#FEF3C7] ring-1 ring-[#CA8A04]/40'}`}
        style={boxStyle}
        title={field.label}
        data-testid={`sign-field-${fieldId}`}
      >
        {hasValue ? (
          <img src={value} alt="Signature" className="max-h-full max-w-full object-contain" />
        ) : (
          <div className="flex items-center gap-1 text-[10px] text-[#92400E] font-medium px-1">
            <Pen className="w-3 h-3" />
            <span className="truncate">Tap to sign</span>
          </div>
        )}
      </button>
    );
  }

  // Checkbox — big tap target
  if (fieldType === 'checkbox') {
    const checked = value === true || value === 'true';
    // Visible box matches the printed square exactly; a transparent finger-sized hit area sits on top.
    const w = bbox.w || 0.015, h = bbox.h || 0.012;
    return (
      <>
        <span
          className={`absolute flex items-center justify-center rounded-[2px] pointer-events-none transition-colors ${checked ? 'bg-[#166534]/10 ring-2 ring-[#166534]' : 'bg-[#FEF3C7]/40 ring-1 ring-[#CA8A04]/50'}`}
          style={{ left: `${bbox.x * 100}%`, top: `${bbox.y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%` }}
        >
          {checked && <Check className="w-full h-full text-[#166534]" strokeWidth={3} />}
        </span>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className="absolute rounded-full hover:bg-[#FEF3C7]/40"
          style={{ left: `calc(${(bbox.x + w / 2) * 100}% - 12px)`, top: `calc(${(bbox.y + h / 2) * 100}% - 12px)`, width: 24, height: 24 }}
          title={field.label}
          aria-pressed={checked}
          data-testid={`sign-field-${fieldId}`}
        />
      </>
    );
  }

  // Select
  if (fieldType === 'select') {
    return (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={`absolute ${ring} rounded px-1 outline-none focus:ring-2 focus:ring-[#0E7490]`}
        style={boxStyle}
        data-testid={`sign-field-${fieldId}`}
      >
        <option value="">{field.placeholder || 'Select…'}</option>
        {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
  }

  // Textarea
  if (fieldType === 'textarea') {
    return (
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={field.label}
        className={`absolute ${ring} focus:ring-2 focus:ring-[#0E7490] rounded px-1 py-0.5 outline-none resize-none`}
        style={{ ...boxStyle, fontFamily: 'inherit' }}
        data-testid={`sign-field-${fieldId}`}
      />
    );
  }

  // text/email/phone/number/date all use <input>
  const inputType = fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : fieldType === 'email' ? 'email' : fieldType === 'phone' ? 'tel' : 'text';
  return (
    <Input
      type={inputType}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.label}
      title={field.patient_field_ref ? `${field.label} · linked to patient record` : field.label}
      className={`absolute ${ring} focus:ring-2 focus:ring-[#0E7490] focus:min-w-[28%] focus:z-10 rounded px-1 outline-none !min-h-0 !py-0 border-0 shadow-none`}
      style={{ ...boxStyle, fontFamily: 'inherit' }}
      data-testid={`sign-field-${fieldId}`}
    />
  );
}

// ---------- Signature modal (canvas) ----------
function SignatureModal({ field, initialValue, onClose, onSave }) {
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(!!initialValue);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Fit to container
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * (window.devicePixelRatio || 1));
    canvas.height = Math.floor(rect.height * (window.devicePixelRatio || 1));
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0A2342';
    if (initialValue) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initialValue;
    }
  }, [initialValue]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointerPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const save = () => {
    if (!hasInk) { toast.error('Draw your signature first'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" data-testid="signature-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-[#0A2342]">Sign: {field.label}</div>
            <div className="text-[11px] text-[#64748B]">Use your finger or stylus to draw your signature</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="sig-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="border-2 border-dashed border-[#CBD5E1] rounded-md bg-[#F8FAFC]" style={{ height: '220px' }}>
          <canvas
            ref={canvasRef}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
            data-testid="signature-canvas"
          />
        </div>
        <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
          <Button variant="outline" onClick={clear} className="flex items-center gap-1" data-testid="sig-clear-btn">
            <RotateCcw className="w-4 h-4" /> Clear
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={save} disabled={!hasInk} data-testid="sig-save-btn">
              <Check className="w-4 h-4 mr-1" /> Save Signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
